import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveFitnessTestResult, updateProfileFlags } from './api/onboarding';
import supabase from './supabaseClient';

const WARMUP_DURATION = 300; // 5 minutes in seconds

function FitnessTest() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('intro');
  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(WARMUP_DURATION);
  const [timerActive, setTimerActive] = useState(false);

  // Test results
  const [testResults, setTestResults] = useState({
    warmup_completed: false,
    warmup_duration_seconds: WARMUP_DURATION,
    push_test_reps: 0,
    push_test_notes: '',
    pull_test_reps: 0,
    pull_test_notes: '',
    legs_test_reps: 0,
    legs_test_notes: '',
    cardio_duration_seconds: 60,
    cardio_intensity: 'moderate',
    cardio_notes: '',
    completed: false,
  });

  // Get current user on mount
  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        setAuthUser(data.user);
      }
    };
    getUser();
  }, []);

  // Warmup timer effect
  useEffect(() => {
    let interval;
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setTimerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSaveAndComplete = async () => {
    if (!authUser) {
      navigate('/login');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const finalResults = {
        ...testResults,
        completed: true,
        test_date: new Date().toISOString(),
      };

      const { error: saveError } = await saveFitnessTestResult(authUser.id, finalResults);
      if (saveError) {
        setError(saveError.message || 'Failed to save test results.');
        setLoading(false);
        return;
      }

      // Update profile flag to mark test as completed
      await updateProfileFlags(authUser.id, { fitness_test_pending: false });

      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const updateResult = (key, value) => {
    setTestResults((prev) => ({ ...prev, [key]: value }));
  };

  // Step: Introduction
  if (currentStep === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-slate-700/50 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-3xl mb-6">
              💪
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Baseline Fitness Test</h1>
            <p className="text-slate-300 text-sm leading-relaxed">
              Complete this quick assessment to help us tailor your workouts and track your progress over time.
            </p>
          </div>

          <div className="bg-slate-700/50 rounded-2xl p-6 border border-slate-600/30 space-y-4">
            <h3 className="text-lg font-semibold text-cyan-400">What to Expect</h3>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-3">
                <span className="text-xl">⏱️</span>
                <div>
                  <span className="font-semibold text-white">5-minute warm-up</span>
                  <p className="text-slate-400">Light cardio to prepare your body</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-xl">💪</span>
                <div>
                  <span className="font-semibold text-white">Strength tests</span>
                  <p className="text-slate-400">Push-ups, pull-ups, and squats</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-xl">🏃</span>
                <div>
                  <span className="font-semibold text-white">Cardio test</span>
                  <p className="text-slate-400">1-minute jumping jacks or running in place</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-slate-700/30 rounded-2xl p-4 border border-cyan-500/30">
            <p className="text-sm text-slate-300 text-center">
              <span className="text-cyan-400 font-semibold">Tip:</span> Do your best, but don't push past your limits. This is a baseline, not a competition.
            </p>
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-700/50 text-red-200 rounded-xl px-4 py-3 text-sm text-center">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="flex-1 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
            >
              Skip for Now
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep('warmup')}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
            >
              Start Test
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Warm-up Timer
  if (currentStep === 'warmup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-slate-700/50 space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Warm-Up</h2>
            <p className="text-slate-300 text-sm">
              Light cardio to get your heart rate up and muscles ready
            </p>
          </div>

          <div className="bg-slate-700/50 rounded-2xl p-8 border border-slate-600/30 text-center">
            <div className="text-7xl font-bold text-cyan-400 mb-4 tabular-nums">
              {formatTime(timeRemaining)}
            </div>
            <p className="text-slate-300 text-sm">
              {timerActive ? 'Keep moving!' : 'Ready to begin?'}
            </p>
          </div>

          <div className="bg-slate-700/30 rounded-2xl p-4 border border-slate-600/30">
            <p className="text-sm text-slate-300">
              <span className="font-semibold text-white">Suggested activities:</span> Walking in place, light jogging, arm circles, leg swings, or gentle jumping jacks
            </p>
          </div>

          <div className="flex gap-3">
            {!timerActive && timeRemaining === WARMUP_DURATION && (
              <button
                type="button"
                onClick={() => setTimerActive(true)}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
              >
                Start Warm-Up
              </button>
            )}
            {timerActive && (
              <button
                type="button"
                onClick={() => setTimerActive(false)}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
              >
                Pause
              </button>
            )}
            {!timerActive && timeRemaining < WARMUP_DURATION && timeRemaining > 0 && (
              <button
                type="button"
                onClick={() => setTimerActive(true)}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
              >
                Resume
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                updateResult('warmup_completed', true);
                updateResult('warmup_duration_seconds', WARMUP_DURATION - timeRemaining);
                setCurrentStep('push');
              }}
              className="flex-1 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
            >
              {timeRemaining === 0 ? 'Continue' : 'Skip Warm-Up'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Push Test
  if (currentStep === 'push') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-slate-700/50 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xl mb-4">
              1/3
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Push Test</h2>
            <p className="text-slate-300 text-sm">
              Complete as many push-ups as you can with good form
            </p>
          </div>

          <div className="bg-slate-700/50 rounded-2xl p-6 border border-slate-600/30 space-y-3">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wide">Instructions</h3>
            <ul className="text-sm text-slate-300 space-y-2 list-disc list-inside">
              <li>Keep your body in a straight line from head to heels</li>
              <li>Lower your chest until your elbows are at 90 degrees</li>
              <li>Push back up to the starting position</li>
              <li>Stop when you can no longer maintain good form</li>
            </ul>
            <p className="text-xs text-slate-400 italic mt-2">
              Modified push-ups (on knees) are perfectly fine!
            </p>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-semibold text-white mb-2 block">How many did you complete?</span>
              <input
                type="number"
                min="0"
                value={testResults.push_test_reps}
                onChange={(e) => updateResult('push_test_reps', parseInt(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 text-white text-lg px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="0"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-white mb-2 block">Notes (optional)</span>
              <input
                type="text"
                value={testResults.push_test_notes}
                onChange={(e) => updateResult('push_test_notes', e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="e.g., modified, felt good, need more practice"
              />
            </label>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setCurrentStep('warmup')}
              className="flex-1 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep('pull')}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Pull Test
  if (currentStep === 'pull') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-slate-700/50 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xl mb-4">
              2/3
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Pull Test</h2>
            <p className="text-slate-300 text-sm">
              Complete as many pull-ups, chin-ups, or inverted rows as you can
            </p>
          </div>

          <div className="bg-slate-700/50 rounded-2xl p-6 border border-slate-600/30 space-y-3">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wide">Instructions</h3>
            <ul className="text-sm text-slate-300 space-y-2 list-disc list-inside">
              <li>Pull-ups: Pull yourself up until your chin is over the bar</li>
              <li>Inverted rows: Pull your chest to a bar or table edge</li>
              <li>Keep movements controlled and avoid swinging</li>
              <li>Stop when you can no longer maintain good form</li>
            </ul>
            <p className="text-xs text-slate-400 italic mt-2">
              No pull-up bar? Use a sturdy table for inverted rows or record 0 and skip.
            </p>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-semibold text-white mb-2 block">How many did you complete?</span>
              <input
                type="number"
                min="0"
                value={testResults.pull_test_reps}
                onChange={(e) => updateResult('pull_test_reps', parseInt(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 text-white text-lg px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="0"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-white mb-2 block">Notes (optional)</span>
              <input
                type="text"
                value={testResults.pull_test_notes}
                onChange={(e) => updateResult('pull_test_notes', e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="e.g., pull-ups, chin-ups, inverted rows"
              />
            </label>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setCurrentStep('push')}
              className="flex-1 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep('legs')}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Legs Test
  if (currentStep === 'legs') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-slate-700/50 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xl mb-4">
              3/3
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Legs Test</h2>
            <p className="text-slate-300 text-sm">
              Complete as many bodyweight squats as you can with good form
            </p>
          </div>

          <div className="bg-slate-700/50 rounded-2xl p-6 border border-slate-600/30 space-y-3">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wide">Instructions</h3>
            <ul className="text-sm text-slate-300 space-y-2 list-disc list-inside">
              <li>Stand with feet shoulder-width apart</li>
              <li>Lower your hips until thighs are parallel to the ground</li>
              <li>Keep your chest up and knees tracking over toes</li>
              <li>Stand back up to the starting position</li>
            </ul>
            <p className="text-xs text-slate-400 italic mt-2">
              Focus on depth and control rather than speed.
            </p>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-semibold text-white mb-2 block">How many did you complete?</span>
              <input
                type="number"
                min="0"
                value={testResults.legs_test_reps}
                onChange={(e) => updateResult('legs_test_reps', parseInt(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 text-white text-lg px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="0"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-white mb-2 block">Notes (optional)</span>
              <input
                type="text"
                value={testResults.legs_test_notes}
                onChange={(e) => updateResult('legs_test_notes', e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="e.g., full depth, partial squats, felt strong"
              />
            </label>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setCurrentStep('pull')}
              className="flex-1 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep('cardio')}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Cardio Test
  if (currentStep === 'cardio') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-slate-700/50 space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Cardio Test</h2>
            <p className="text-slate-300 text-sm">
              Perform jumping jacks or run in place for 1 minute
            </p>
          </div>

          <div className="bg-slate-700/50 rounded-2xl p-6 border border-slate-600/30 space-y-3">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wide">Instructions</h3>
            <ul className="text-sm text-slate-300 space-y-2 list-disc list-inside">
              <li>Perform jumping jacks or run in place continuously for 60 seconds</li>
              <li>Maintain a steady pace that challenges you but is sustainable</li>
              <li>Note how you feel at the end - this is your intensity level</li>
            </ul>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-semibold text-white mb-2 block">How did it feel?</span>
              <select
                value={testResults.cardio_intensity}
                onChange={(e) => updateResult('cardio_intensity', e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="low">Low - Could talk easily</option>
                <option value="moderate">Moderate - Breathing hard but manageable</option>
                <option value="high">High - Very challenging, hard to talk</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-white mb-2 block">Notes (optional)</span>
              <input
                type="text"
                value={testResults.cardio_notes}
                onChange={(e) => updateResult('cardio_notes', e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="e.g., completed full minute, had to slow down"
              />
            </label>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setCurrentStep('legs')}
              className="flex-1 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep('results')}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
            >
              See Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Results
  if (currentStep === 'results') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-slate-700/50 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 text-4xl mb-6">
              🎉
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Test Complete!</h1>
            <p className="text-slate-300 text-sm leading-relaxed">
              Great job completing your baseline fitness test. Here's what you accomplished:
            </p>
          </div>

          <div className="bg-slate-700/50 rounded-2xl p-6 border border-slate-600/30 space-y-4">
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Your Results</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-600/30">
                <div className="text-3xl font-bold text-white">{testResults.push_test_reps}</div>
                <div className="text-sm text-slate-300">Push-ups</div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-600/30">
                <div className="text-3xl font-bold text-white">{testResults.pull_test_reps}</div>
                <div className="text-sm text-slate-300">Pull-ups/Rows</div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-600/30">
                <div className="text-3xl font-bold text-white">{testResults.legs_test_reps}</div>
                <div className="text-sm text-slate-300">Squats</div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-600/30">
                <div className="text-3xl font-bold text-white capitalize">{testResults.cardio_intensity}</div>
                <div className="text-sm text-slate-300">Cardio Intensity</div>
              </div>
            </div>
          </div>

          <div className="bg-cyan-500/10 rounded-2xl p-4 border border-cyan-500/30">
            <p className="text-sm text-slate-300 text-center">
              <span className="text-cyan-400 font-semibold">Next step:</span> We'll use these results to customize your workout plans and track your progress over time.
            </p>
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-700/50 text-red-200 rounded-xl px-4 py-3 text-sm text-center">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleSaveAndComplete}
            disabled={loading}
            className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
          >
            {loading ? 'Saving...' : 'Save Results & Continue to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export default FitnessTest;
